/**
 * RecipesScreen.jsx
 * Recipe browser with REAL DATA from backend, search, filtering, and detail view
 */
import React, { useState, useEffect } from 'react';
import { 
  Search, Utensils, Clock, Users, ChefHat, RefreshCw, Heart, HeartOff,
  ShoppingCart, ChevronRight, X, Plus, Check, Flame, Beef, Apple, Cookie
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const RecipesScreen = ({ portalToken, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [addingToShoppingList, setAddingToShoppingList] = useState(false);

  const { t } = useTranslation();

  const categories = [
    { id: 'all', label: t('recipes.all'), icon: Utensils },
    { id: 'breakfast', label: t('recipes.categories'), icon: Apple },
    { id: 'lunch', label: 'Lunch', icon: Beef },
    { id: 'dinner', label: 'Dinner', icon: ChefHat },
    { id: 'snack', label: 'Snacks', icon: Cookie }
  ];

  useEffect(() => {
    if (portalToken) {
      loadRecipes();
      loadSavedRecipes();
    }
  }, [portalToken]);

  useEffect(() => {
    filterRecipes();
  }, [recipes, searchQuery, selectedCategory]);

  const loadRecipes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/portal/recipes`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setRecipes(response.data.recipes || []);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSavedRecipes = async () => {
    try {
      const response = await axios.get(
        `${API}/portal/recipes/saved`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setSavedRecipes(response.data.recipe_ids || []);
    } catch (error) {
      // Silent fail
    }
  };

  const filterRecipes = () => {
    let filtered = [...recipes];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }
    
    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.name?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.tags?.some(t => t.toLowerCase().includes(query))
      );
    }
    
    setFilteredRecipes(filtered);
  };

  const toggleSaveRecipe = async (recipeId) => {
    setSavingRecipe(true);
    const isSaved = savedRecipes.includes(recipeId);
    
    try {
      if (isSaved) {
        await axios.delete(
          `${API}/portal/recipes/saved/${recipeId}`,
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
        setSavedRecipes(prev => prev.filter(id => id !== recipeId));
        toast.success('Rezept entfernt');
      } else {
        await axios.post(
          `${API}/portal/recipes/saved/${recipeId}`,
          {},
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
        setSavedRecipes(prev => [...prev, recipeId]);
        toast.success('Rezept gespeichert');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingRecipe(false);
    }
  };

  const addToShoppingList = async (recipe) => {
    setAddingToShoppingList(true);
    try {
      await axios.post(
        `${API}/portal/shopping-list/from-recipe/${recipe.id}`,
        { servings: recipe.servings },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success('Zutaten zur Einkaufsliste hinzugefügt');
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setAddingToShoppingList(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'Einfach';
      case 'medium': return 'Mittel';
      case 'hard': return 'Anspruchsvoll';
      default: return difficulty;
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat?.label || category;
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/30 to-emerald-500/20 flex items-center justify-center">
            <ChefHat className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('recipes.title')}</h1>
            <p className="text-zinc-400 text-xs">{recipes.length} Rezepte verfügbar</p>
          </div>
        </div>
        <button 
          onClick={loadRecipes}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
    
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input 
          type="text" 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rezept suchen..."
          className="w-full bg-zinc-800 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-yellow-500/50"
        />
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {categories.map(cat => (
          <button 
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap flex items-center gap-1.5 ${
              selectedCategory === cat.id 
                ? 'bg-yellow-500 text-black font-medium' 
                : 'bg-zinc-800 text-zinc-300'
            }`}
          >
            <cat.icon className="w-3.5 h-3.5" />
            {cat.label}
          </button>
        ))}
      </div>

      {/* Recipes List */}
      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-green-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">{t('recipes.title')}...</p>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="bg-zinc-800/50 rounded-2xl p-6 text-center">
          <Utensils className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-white font-medium">{t('recipes.no_recipes')}</p>
          <p className="text-zinc-400 text-sm mt-1">
            {searchQuery ? 'Versuche einen anderen Suchbegriff' : 'Keine Rezepte in dieser Kategorie'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecipes.map(recipe => (
            <button
              key={recipe.id}
              onClick={() => setSelectedRecipe(recipe)}
              className="w-full bg-zinc-800/50 rounded-xl p-3 flex gap-3 text-left"
            >
              {/* Recipe Image/Icon */}
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 flex items-center justify-center flex-shrink-0">
                {recipe.image_url ? (
                  <img src={recipe.image_url} alt={recipe.name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Utensils className="w-8 h-8 text-green-400" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-white font-medium text-sm truncate">{recipe.name}</h3>
                  {savedRecipes.includes(recipe.id) && (
                    <Heart className="w-4 h-4 text-red-400 fill-red-400 flex-shrink-0" />
                  )}
                </div>
                <p className="text-zinc-500 text-xs mt-0.5">{getCategoryLabel(recipe.category)}</p>
                
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {recipe.nutrition_per_serving?.calories && (
                    <span className="text-yellow-400 text-xs flex items-center gap-0.5">
                      <Flame className="w-3 h-3" />
                      {Math.round(recipe.nutrition_per_serving.calories)} kcal
                    </span>
                  )}
                  {recipe.nutrition_per_serving?.protein && (
                    <span className="text-blue-400 text-xs">{recipe.nutrition_per_serving.protein}g P</span>
                  )}
                  {(recipe.prep_time || recipe.cook_time) && (
                    <span className="text-zinc-500 text-xs flex items-center gap-0.5">
                      <Clock className="w-3 h-3" />
                      {(recipe.prep_time || 0) + (recipe.cook_time || 0)} Min
                    </span>
                  )}
                </div>
                
                {/* Tags */}
                {recipe.tags?.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {recipe.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 text-[10px] rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <ChevronRight className="w-5 h-5 text-zinc-500 self-center flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
          {/* Header */}
          <div className="p-4 flex items-center justify-between border-b border-zinc-800">
            <h2 className="text-white font-bold text-lg truncate flex-1 mr-4">{selectedRecipe.name}</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleSaveRecipe(selectedRecipe.id)}
                disabled={savingRecipe}
                className="p-2 bg-zinc-800 rounded-lg"
              >
                {savedRecipes.includes(selectedRecipe.id) ? (
                  <Heart className="w-5 h-5 text-red-400 fill-red-400" />
                ) : (
                  <HeartOff className="w-5 h-5 text-zinc-400" />
                )}
              </button>
              <button
                onClick={() => setSelectedRecipe(null)}
                className="p-2 bg-zinc-800 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Hero Image */}
            <div className="w-full h-40 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-600/20 flex items-center justify-center">
              {selectedRecipe.image_url ? (
                <img src={selectedRecipe.image_url} alt={selectedRecipe.name} className="w-full h-full object-cover rounded-xl" />
              ) : (
                <ChefHat className="w-16 h-16 text-green-400" />
              )}
            </div>
            
            {/* Description */}
            {selectedRecipe.description && (
              <p className="text-zinc-300 text-sm">{selectedRecipe.description}</p>
            )}
            
            {/* Quick Info */}
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
                <Clock className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">
                  {(selectedRecipe.prep_time || 0) + (selectedRecipe.cook_time || 0)}
                </p>
                <p className="text-zinc-500 text-[10px]">{t('recipes.min')}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
                <Users className="w-4 h-4 text-zinc-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">{selectedRecipe.servings || 2}</p>
                <p className="text-zinc-500 text-[10px]">{t('recipes.servings')}</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
                <Flame className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
                <p className="text-white text-sm font-medium">{selectedRecipe.nutrition_per_serving?.calories || '-'}</p>
                <p className="text-zinc-500 text-[10px]">kcal</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
                <ChefHat className={`w-4 h-4 ${getDifficultyColor(selectedRecipe.difficulty)} mx-auto mb-1`} />
                <p className={`text-sm font-medium ${getDifficultyColor(selectedRecipe.difficulty)}`}>
                  {getDifficultyLabel(selectedRecipe.difficulty)?.charAt(0)}
                </p>
                <p className="text-zinc-500 text-[10px]">Level</p>
              </div>
            </div>
            
            {/* Nutrition */}
            {selectedRecipe.nutrition_per_serving && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium text-sm mb-3">{t('recipes.per_serving')}</h3>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div>
                    <p className="text-yellow-400 font-bold">{selectedRecipe.nutrition_per_serving.calories}</p>
                    <p className="text-zinc-500 text-xs">kcal</p>
                  </div>
                  <div>
                    <p className="text-blue-400 font-bold">{selectedRecipe.nutrition_per_serving.protein}g</p>
                    <p className="text-zinc-500 text-xs">Protein</p>
                  </div>
                  <div>
                    <p className="text-green-400 font-bold">{selectedRecipe.nutrition_per_serving.carbs}g</p>
                    <p className="text-zinc-500 text-xs">Carbs</p>
                  </div>
                  <div>
                    <p className="text-orange-400 font-bold">{selectedRecipe.nutrition_per_serving.fat}g</p>
                    <p className="text-zinc-500 text-xs">Fett</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Ingredients */}
            {selectedRecipe.ingredients?.length > 0 && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-white font-medium text-sm">{t('recipes.ingredients')}</h3>
                  <button
                    onClick={() => addToShoppingList(selectedRecipe)}
                    disabled={addingToShoppingList}
                    className="text-yellow-400 text-xs flex items-center gap-1"
                  >
                    {addingToShoppingList ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <ShoppingCart className="w-3 h-3" />
                        Zur Einkaufsliste
                      </>
                    )}
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedRecipe.ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2 py-1.5 border-b border-zinc-700/30 last:border-0">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-zinc-300 text-sm flex-1">{ing.name}</span>
                      <span className="text-zinc-500 text-sm">
                        {ing.amount} {ing.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Instructions */}
            {selectedRecipe.instructions?.length > 0 && (
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-medium text-sm mb-3">{t('recipes.instructions')}</h3>
                <div className="space-y-3">
                  {selectedRecipe.instructions.map((step, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 text-xs flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </span>
                      <p className="text-zinc-300 text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer Action */}
          <div className="p-4 border-t border-zinc-800">
            <button
              onClick={() => addToShoppingList(selectedRecipe)}
              disabled={addingToShoppingList}
              className="w-full py-3 bg-yellow-500 text-black rounded-xl font-medium flex items-center justify-center gap-2"
            >
              {addingToShoppingList ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Zutaten zur Einkaufsliste
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipesScreen;
